import { useState, useEffect } from "react";
import classNames from "classnames";
import { EnvelopeIcon, KeyIcon } from "@heroicons/react/24/solid";

interface Props {
  placeholder: string;
  type: "email" | "password" | "text";
  state?: "default" | "error";
  value: string;
  onChange: (value: string) => void;
  name: string;
  icon?: (props: { className: string }) => JSX.Element;
  style?: "default" | "outline";
  error?: string | null;
}

export const inputErrorBorderColorClassName = "border-red-500";

export default function Input({
  placeholder,
  type,
  state = "default",
  value,
  onChange,
  name,
  icon: iconFromProps,
  style = "default",
  error,
}: Props) {
  const [focused, setFocused] = useState(false);
  const iconClassName = classNames("h-6", state === "error" && "text-red-500");
  const icon = (() => {
    if (iconFromProps) {
      return iconFromProps({ className: iconClassName });
    }

    if (type === "password") {
      return <KeyIcon className={iconClassName} />;
    }

    return <EnvelopeIcon className={iconClassName} />;
  })();

  const active = value.length > 0 || focused;

  return (
    <div className={classNames(state === "error" && " rounded")}>
      <div
        className={classNames(
          "flex transition-all",
          style === "default" && [
            active ? "bg-white text-black" : "bg-white/90 text-slate-500",
            state === "error" && "bg-red-100 text-red-500",
          ],

          style === "outline" && [
            "border",
            active ? "bg-black text-white" : "bg-black text-white/50",
            state === "error"
              ? `text-red-500 ${inputErrorBorderColorClassName}`
              : "border-white",
          ],

          error && state === "error" ? ["rounded-t"] : ["rounded"]
        )}
      >
        <div
          className={classNames(
            "flex items-center justify-center rounded-l pl-2 pr-0"
          )}
        >
          {icon}
        </div>

        <input
          name={name}
          onChange={(event) => onChange(event.target.value ?? "")}
          value={value}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          type={type}
          placeholder={placeholder}
          className={
            "h-full w-full rounded-r bg-transparent px-2 py-3 font-bold outline-none placeholder:font-normal placeholder:text-black/50"
          }
        />
      </div>

      {error && (
        <div
          className={
            "bg-red-500 text-white rounded-b text-xs px-1 py-0.5 text-center"
          }
        >
          {error}
        </div>
      )}
    </div>
  );
}
